import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import { capitalize, isEmpty } from 'lodash';
import compact from 'lodash/compact.js';
import { match } from 'ts-pattern';
import { jsDateToZonedDateTime } from './data/date.js';
import { namePrefixDisplay } from './data/namePrefixes.js';
import { nameSuffixDisplay } from './data/nameSuffixes.js';
export const formatNumberAsOrdinal = (number) => {
    const digit = number % 10;
    const lastTwoDigits = number % 100;
    if (digit === 1 && lastTwoDigits !== 11)
        return `${number}st`;
    if (digit === 2 && lastTwoDigits !== 12)
        return `${number}nd`;
    if (digit === 3 && lastTwoDigits !== 13)
        return `${number}rd`;
    return `${number}th`;
};
export const isJSONPrimitive = (value) => {
    return (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean');
};
// Check to see if the provided string is strigified JSON
export const isJSONString = (string) => {
    if (typeof string !== 'string')
        return false;
    try {
        JSON.parse(string);
        return true;
        // oxlint-disable-next-line typescript/no-unused-vars
    }
    catch (error) {
        return false;
    }
};
/**
 * Use to generate a type error if code that is expected to be unreachable is actually reachable
 *
 * reference: https://stackoverflow.com/a/39419171
 */
export function assertUnreachable(_, message = "Didn't expect to get here") {
    throw new Error(message);
}
/**
 * converts object into an array of entries, each with a `key` and the rest of that key's value
 * resulting type is a discriminated union on possible values of 'key' wherekeys are taken from `Object.keys(obj)`
 */
export const objectToKeyedArray = (obj) => Object.keys(obj).map((k) => ({
    key: k,
    ...obj[k],
}));
export const typedEntries = (obj) => {
    return Object.entries(obj);
};
export const nameValueArrayFromObject = (object) => {
    return Object.entries(object).map(([key, value]) => ({
        name: value,
        value: key,
    }));
};
export const getBase64ContentFromDataUri = (dataUri) => {
    const matches = dataUri.match(/^data:.*?;base64,(.*)$/);
    return matches ? matches[1] : '';
};
export const formatMoneyInCents = (value, options) => {
    const defaultValue = options?.defaultValue ?? '';
    if (value === 0 && options?.zeroValue)
        return options.zeroValue;
    if (value == null)
        return defaultValue;
    const prefix = value < 0 ? '-' : '';
    return [
        prefix,
        '$',
        (Math.abs(value) / 100).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }),
    ].join('');
};
export const formatNullableMoneyInCents = (value) => value == null ? '' : formatMoneyInCents(value);
export const formatDollarsToCents = (value) => {
    return Math.round(Number(value) * 100);
};
export function formatDisplayName(person, options = { showPreferredName: false }) {
    if (isEmpty(person))
        return '';
    const personName = [
        person.namePrefix ? namePrefixDisplay[person.namePrefix] : null,
        person.givenName,
        person.middleName,
        person.familyName,
        person.nameSuffix ? nameSuffixDisplay[person.nameSuffix] : null,
    ]
        .filter(Boolean)
        .join(' ');
    const preferredName = options.showPreferredName && person.preferredName
        ? `(${person.preferredName})`
        : undefined;
    const name = [personName, person.credentials].filter(Boolean).join(', ');
    return [name, preferredName].filter(Boolean).join(' ');
}
export function formatPatientBrowserTabTitle(person) {
    if (isEmpty(person))
        return '';
    const abbreviatedFamilyName = person.familyName?.[0]
        ? `${person.familyName[0]}.`
        : null;
    const name = [person.givenName, abbreviatedFamilyName]
        .filter(Boolean)
        .join(' ');
    return person.preferredName ? `${name} (${person.preferredName})` : name;
}
export const toDecimal = (value, precision = 2) => {
    if (!Number(value))
        return null;
    return Number(Number(value).toFixed(precision)); // Outside Number() allows us to only show only as many zeros as needed
};
export var AddressFormat;
(function (AddressFormat) {
    AddressFormat["ONE_LINE"] = "one-line";
    AddressFormat["TWO_LINES"] = "two-lines";
})(AddressFormat || (AddressFormat = {}));
export const formatAddressLine1 = (address) => {
    if (isEmpty(address))
        return '';
    return compact([address.line1, address.line2]).join(', ');
};
export const formatPostalCode = (address) => compact([address?.postalCode, address?.zipPlusFour]).join('-');
export const formatAddressLine2 = (address) => {
    if (isEmpty(address))
        return '';
    return compact([
        `${address.city}, ${address.state}`,
        formatPostalCode(address),
    ]).join(' ');
};
export const formatAddress = ({ address, options, }) => {
    const format = options?.format ?? AddressFormat.ONE_LINE;
    const includeCountry = options?.includeCountry ?? false;
    if (!address) {
        return '';
    }
    return match(format)
        .with(AddressFormat.ONE_LINE, () => compact([
        address.line1,
        address.line2,
        address.city,
        `${address.state} ${formatPostalCode(address)}`,
        includeCountry ? address.country : null,
    ]).join(', '))
        .with(AddressFormat.TWO_LINES, () => compact([
        formatAddressLine1(address),
        formatAddressLine2(address),
        includeCountry ? address.country : null,
    ]).join('\n'))
        .exhaustive();
};
export const truncateWithEllipsis = (str, opts) => (str.length > opts.maxLength ? `${str.slice(0, opts.maxLength)}...` : str);
// Use to format a list of items into a comma separated list with the word "and" between the last two items
export const commaSeparatedList = (items) => {
    return new Intl.ListFormat('en', {
        style: 'long',
        type: 'conjunction',
    }).format(items);
};
/**
 * Given a list of items with a value, takes from the list until the total amount
 * is reached or the list is exhausted.  This is commonly used for making payments against
 * claims or charges. Ex. I want to pay $100.  Keep paying off claims until the full $100 is spent.
 *
 * @param {object} args
 * @param {T[]} args.items - The list of items to take from
 * @param {number} args.amount - The amount to take from the items
 * @param {(item: T) => number} args.itemToAmount - A function that computes the amount that can be taken from an item
 * @returns {{ items: { item: T; amount: number }[]; amountRemaining: number }} - The list of items taken along with the amount taken from each item, and the amount remaining
 */
export const takeUpToAmount = ({ items, amount, itemToAmount, }) => {
    let amountRemaining = amount;
    const itemsToTake = [];
    for (const item of items) {
        const amountToTake = Math.min(itemToAmount(item), amountRemaining);
        if (amountToTake > 0) {
            itemsToTake.push({ item, amount: amountToTake });
            amountRemaining -= amountToTake;
        }
    }
    return {
        items: itemsToTake,
        amountRemaining,
    };
};
export function formatPaymentMethodDisplay(paymentMethod, options) {
    const format = options?.format ?? 'combined';
    const fallback = options?.fallback ?? 'Card on file';
    if (!paymentMethod) {
        return format === 'split'
            ? { primary: fallback, secondary: null }
            : fallback;
    }
    const { brand, last4, expirationMonth, expirationYear, holderName } = paymentMethod;
    const primaryInfo = capitalize([brand, last4 ? `ending in ${last4}` : null].filter(Boolean).join(' '));
    const secondaryParts = [
        holderName,
        expirationMonth != null && expirationYear != null
            ? `${expirationMonth}/${expirationYear}`
            : null,
    ].filter(Boolean);
    const secondaryInfo = secondaryParts.length > 0 ? secondaryParts.join(' • ') : null;
    if (format === 'split') {
        return {
            primary: primaryInfo || fallback,
            secondary: secondaryInfo,
        };
    }
    const parts = [primaryInfo, ...secondaryParts].filter(Boolean);
    return parts.length > 0 ? parts.join(' • ') : fallback;
}
export const isPaymentMethodExpired = (paymentMethod, currentDate = LocalDate.now()) => {
    const { expirationMonth, expirationYear } = paymentMethod ?? {};
    if (expirationMonth == null || expirationYear == null)
        return false;
    const expirationMonthStart = LocalDate.of(expirationYear, expirationMonth, 1);
    const currentMonthStart = currentDate.withDayOfMonth(1);
    return expirationMonthStart.isBefore(currentMonthStart);
};
export const formatInitials = ({ givenName, familyName, }) => [(givenName ?? '')[0], (familyName ?? '')[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();
export const formatLastEditedBy = ({ lastEditedAt, lastEditedBy, location, }) => {
    if (!lastEditedAt || !lastEditedBy) {
        return;
    }
    const zonedDateTime = jsDateToZonedDateTime(lastEditedAt, location.timezone);
    const name = formatDisplayName(lastEditedBy);
    return `Last edited by ${name} on ${zonedDateTime.format(DateTimeFormatter.ofPattern("M/d/yyyy 'at' h:mm a z").withLocale(Locale.US))}`;
};
/**
 * Formats a person's name as <given name> <first initial of family name>.
 *
 * example: "John D."
 *
 * @param person - The person object containing the given name and family name.
 * @returns The formatted name string.
 */
export const formatNameFirstWithLastInitial = (person, suffix) => `${person?.givenName} ${person?.familyName[0]}${suffix ?? '.'}`;
export const formatPhoneNumber = (phoneString) => {
    if (isEmpty(phoneString) || !phoneString) {
        return null;
    }
    return parsePhoneNumber(phoneString, 'US').formatNational();
};
export const safeFormatPhoneNumber = (phoneNumber) => {
    try {
        return formatPhoneNumber(phoneNumber) ?? phoneNumber;
    }
    catch {
        return phoneNumber;
    }
};
/**
 * Normalizes a phone number string to E.164 format (e.g. "+15555555555").
 * Accepts numbers with or without the leading "+", 10-digit national numbers,
 * and 11-digit numbers with country code. Returns the original string if
 * parsing fails.
 */
export const normalizePhoneE164 = (phoneString) => {
    try {
        return parsePhoneNumber(phoneString, 'US').format('E.164');
    }
    catch {
        return phoneString;
    }
};
export const extractPhoneNumberDigits = ({ query, }) => {
    const digits = query.replace(/\D/g, '');
    const isPhoneLike = /^[\d\s()+-]+$/.test(query) && digits.length >= 3;
    return isPhoneLike ? digits : null;
};
// computed styles report colors as rgb(), never the authored hex
// hex pairs map to red, green, blue as in color.js by the CSS Color spec editors
// https://github.com/color-js/color.js
export const hexToRgb = (hex) => {
    const red = Number(`0x${hex.slice(1, 3)}`);
    const green = Number(`0x${hex.slice(3, 5)}`);
    const blue = Number(`0x${hex.slice(5, 7)}`);
    return `rgb(${red}, ${green}, ${blue})`;
};
//# sourceMappingURL=utils.js.map